/**
 * NativeTimeInput - Native HTML time picker for mobile devices
 *
 * This component renders a native HTML5 time input (`<input type="time">`) with proper
 * Temporal API integration. It's specifically designed for mobile devices where native
 * time pickers provide better UX than custom JavaScript implementations.
 *
 * **Key Features:**
 * - Seamless Temporal API integration for modern date/time handling
 * - Min/max time constraints with native validation
 * - Step interval support (converted to seconds for HTML5)
 * - Comprehensive accessibility with ARIA attributes
 * - Validation error display with role="alert"
 * - Ref forwarding for programmatic control
 *
 * **Usage Context:**
 * - Primary input for SmartTimeInput on mobile devices
 * - Used when `nativePicker` is set to 'always'
 * - Preferred over custom dropdowns for native mobile experience
 *
 * @example
 * ```tsx
 * const [time, setTime] = useState<Temporal.PlainTime | null>(null)
 *
 * <NativeTimeInput
 *   value={time}
 *   onChange={(e) => {
 *     const newTime = Temporal.PlainTime.from(e.target.value)
 *     setTime(newTime)
 *   }}
 *   min={Temporal.PlainTime.from('09:00')}
 *   max={Temporal.PlainTime.from('17:00')}
 *   step={15}
 *   aria-label="Select meeting time"
 * />
 * ```
 *
 * @example
 * ```tsx
 * // With validation error
 * <NativeTimeInput
 *   value={time}
 *   onChange={handleChange}
 *   validationError={{
 *     type: 'out-of-range',
 *     message: 'Time must be between 9 AM and 5 PM',
 *     originalInput: '18:00'
 *   }}
 * />
 * ```
 */

import * as React from 'react'
import type { Temporal } from '@js-temporal/polyfill'

import { Input } from '../../Input'
import { Text } from '../../Text'
import type { ValidationError } from '../types'
import { formatPlainTimeForNativeInput } from '../utils/formatters'
import styles from '../SmartTimeInput.module.css'

/**
 * Props for the NativeTimeInput component
 *
 * Extends HTML input attributes while providing type-safe Temporal API integration
 * for time values and constraints.
 */
export interface NativeTimeInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  | 'value'
  | 'onChange'
  | 'onBlur'
  | 'size'
  | 'prefix'
  | 'suffix'
  | 'min'
  | 'max'
  | 'step'
> {
  value: Temporal.PlainTime | null
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
  disabled?: boolean
  readOnly?: boolean
  required?: boolean
  size?: 'small' | 'medium' | 'large'
  id?: string
  name?: string
  'aria-label'?: string
  'aria-describedby'?: string
  min?: Temporal.PlainTime
  max?: Temporal.PlainTime
  step?: number
  error?: boolean
  validationError?: ValidationError | null
  prefix?: React.ReactNode
  suffix?: React.ReactNode
  className?: string
  style?: React.CSSProperties
}

export const NativeTimeInput = React.forwardRef<
  HTMLInputElement,
  NativeTimeInputProps
>(
  (
    {
      value,
      onChange,
      onBlur,
      disabled = false,
      readOnly = false,
      required = false,
      size = 'medium',
      id,
      name,
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedBy,
      min,
      max,
      step,
      error = false,
      validationError = null,
      prefix,
      suffix,
      className,
      style,
      ...otherProps
    },
    ref,
  ) => {
    const internalErrorId = React.useId()
    const errorId = `${internalErrorId}-error`

    const nativeValue = value ? formatPlainTimeForNativeInput(value) : ''
    const nativeMin = min ? formatPlainTimeForNativeInput(min) : undefined
    const nativeMax = max ? formatPlainTimeForNativeInput(max) : undefined
    const nativeStep = step ? step * 60 : undefined

    const hasError = error || validationError !== null

    return (
      <div className={styles.container}>
        <Input
          ref={ref}
          type="time"
          value={nativeValue}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          aria-invalid={hasError ? 'true' : 'false'}
          aria-errormessage={validationError ? errorId : undefined}
          size={size}
          id={id}
          name={name}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          min={nativeMin}
          max={nativeMax}
          step={nativeStep}
          prefix={prefix}
          suffix={suffix}
          className={className}
          style={style}
          {...otherProps}
        />

        {validationError ? (
          <Text
            id={errorId}
            size="sm"
            color="critical"
            className={styles.errorMessage}
            role="alert"
          >
            {validationError.message}
          </Text>
        ) : null}
      </div>
    )
  },
)

NativeTimeInput.displayName = 'NativeTimeInput'
